import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useClerk } from "@clerk/clerk-react";
import { useState } from "react";
import { clerkErrorMessage } from "./clerk-errors.js";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

/** Confirm before ending the session — used from profile menu and Security. */
export function SignOutConfirm({ open, onOpenChange }: Props) {
	const { signOut } = useClerk();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const confirm = async () => {
		setBusy(true);
		setError(null);
		try {
			await signOut({ redirectUrl: "/" });
		} catch (err) {
			const message = clerkErrorMessage(err);
			if (message) setError(message);
			setBusy(false);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (busy) return;
				setError(null);
				onOpenChange(next);
			}}
		>
			<DialogContent className="sign-out-confirm sm:max-w-md" showCloseButton>
				<DialogHeader>
					<DialogTitle>Sign out?</DialogTitle>
					<DialogDescription>
						You’ll need to sign in again to use this workspace on this
						device.
					</DialogDescription>
				</DialogHeader>
				{error && (
					<p className="settings-form-error m-0" role="alert">
						{error}
					</p>
				)}
				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						disabled={busy}
						onClick={() => onOpenChange(false)}
					>
						Cancel
					</Button>
					<Button
						type="button"
						variant="destructive"
						disabled={busy}
						onClick={() => void confirm()}
					>
						{busy ? "Signing out…" : "Sign out"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
